// Chat screen — header, scrollable history, error banner, suggestions panel,
// and the typing input. Owns the per-screen scroll behaviour and a11y wiring.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Library, RotateCcw, MessageCircle, Sparkles, Star } from 'lucide-react';
import type { ChatState, LibraryWord, Persona, PersonaMemory, Word } from '@/lib/types';
import { Portrait } from '@/components/portraits';
import { PersonaProfileSheet } from '@/components/PersonaProfileSheet';
import { ChatBubble } from './ChatBubble';
import { ReplyOptions } from './ReplyOptions';
import { TypingReply } from './TypingReply';
import { useChat } from './useChat';
import { getAIClient } from '@/lib/ai/client';
import { C } from '@/styles/tokens';

/** Run a memory extraction every N completed round-trips per persona. */
const MEMORY_EXTRACT_THRESHOLD = 10;

interface ChatScreenProps {
  persona: Persona;
  state: ChatState;
  library: LibraryWord[];
  memory: PersonaMemory | undefined;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onBumpMemoryCounter: (personaId: string) => void;
  onUpdatePersonaMemory: (personaId: string, facts: string[]) => void;
  onBack: () => void;
  onAppendMessage: (personaId: string, msg: ChatState['history'][number]) => void;
  onClearStoryContext: (personaId: string) => void;
  onSaveWord: (personaId: string, word: Word) => void;
  onOpenLibrary: () => void;
}

export function ChatScreen({
  persona,
  state,
  library,
  memory,
  isFavorite,
  onToggleFavorite,
  onBumpMemoryCounter,
  onUpdatePersonaMemory,
  onBack,
  onAppendMessage,
  onClearStoryContext,
  onSaveWord,
  onOpenLibrary,
}: ChatScreenProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const memoryInFlight = useRef(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // After every successful AI reply: bump the per-persona counter and, when
  // we hit the threshold, fire a background memory extraction. The extraction
  // does not block the UI — the next chat reply just won't see fresher facts
  // until the call resolves and the snapshot updates.
  const handleTurnComplete = useCallback(() => {
    const projected = (memory?.msgsSinceExtract ?? 0) + 1;
    onBumpMemoryCounter(persona.id);
    if (projected < MEMORY_EXTRACT_THRESHOLD || memoryInFlight.current) return;

    memoryInFlight.current = true;
    void (async () => {
      try {
        const facts = await getAIClient().extractMemory({
          persona,
          currentFacts: memory?.facts ?? [],
          history: state.history,
        });
        if (facts.length > 0) onUpdatePersonaMemory(persona.id, facts);
      } catch (err) {
        console.warn('[memory] extract failed:', err);
      } finally {
        memoryInFlight.current = false;
      }
    })();
  }, [persona, memory, state.history, onBumpMemoryCounter, onUpdatePersonaMemory]);

  const chat = useChat({
    persona,
    state,
    library,
    memoryFacts: memory?.facts,
    appendMessage: (msg) => onAppendMessage(persona.id, msg),
    clearStoryContext: () => onClearStoryContext(persona.id),
    onTurnComplete: handleTurnComplete,
  });

  // Auto-scroll on history changes, loading flips, and error banner toggles.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [state.history.length, chat.loading, chat.error]);

  return (
    <section className="h-full flex flex-col" style={{ background: C.cream }}>
      <header
        className="relative flex items-center gap-3 px-4 py-3 safe-top bg-yunwen"
        style={{ backgroundColor: C.cream, borderBottom: `1px solid ${C.blush}` }}
      >
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          aria-label="Back"
          style={{ background: C.paper }}
        >
          <ArrowLeft size={18} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => setProfileOpen(true)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left rounded-full"
          aria-label={`View ${persona.name}'s profile`}
        >
          <span className="block w-10 h-10 rounded-full overflow-hidden flex-shrink-0" aria-hidden>
            <Portrait persona={persona} />
          </span>
          <div className="min-w-0">
            <div className="ff-zh-serif text-lg font-bold leading-none truncate">{persona.name}</div>
            <div className="ff-body text-[11px] truncate" style={{ color: C.inkLight }}>
              {persona.pinyin} · {persona.level.tag}
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={onToggleFavorite}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          aria-label={isFavorite ? `Unstar chat with ${persona.name}` : `Star chat with ${persona.name}`}
          aria-pressed={isFavorite}
          style={{ background: C.paper }}
        >
          <Star
            size={18}
            aria-hidden
            fill={isFavorite ? C.gold : 'none'}
            color={isFavorite ? C.gold : C.ink}
          />
        </button>
        <button
          type="button"
          onClick={onOpenLibrary}
          className="w-9 h-9 rounded-full flex items-center justify-center"
          aria-label="Open library"
          style={{ background: C.paper }}
        >
          <Library size={18} aria-hidden />
        </button>
      </header>

      {state.lastStory && (
        <div className="mx-4 mt-3 p-3 rounded-2xl flex items-start gap-3 anim-scale-in" style={{ background: C.blush }}>
          <MessageCircle size={16} style={{ color: C.vermillionDeep }} aria-hidden />
          <div className="flex-1 ff-body text-[12px]" style={{ color: C.ink }}>
            Replying to <span className="ff-zh-serif">{state.lastStory.hz}</span>
            <span className="opacity-60 ml-1">· {state.lastStory.captionEn ?? state.lastStory.en}</span>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5" aria-live="polite">
        {state.history.map((m) => (
          <ChatBubble
            key={m.id}
            msg={m}
            side={m.from === 'me' ? 'right' : 'left'}
            library={library}
            onSaveWord={(w) => onSaveWord(persona.id, w)}
            accent={persona.accent}
          />
        ))}
        {chat.loading && (
          <div className="flex justify-start">
            <div
              className="px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5"
              style={{ background: 'white', boxShadow: '0 2px 8px rgba(26,22,20,0.06)' }}
              role="status"
              aria-label="Persona is typing"
            >
              <span className="w-2 h-2 rounded-full anim-bounce-dot" style={{ background: persona.accent }} />
              <span
                className="w-2 h-2 rounded-full anim-bounce-dot"
                style={{ background: persona.accent, animationDelay: '0.2s' }}
              />
              <span
                className="w-2 h-2 rounded-full anim-bounce-dot"
                style={{ background: persona.accent, animationDelay: '0.4s' }}
              />
            </div>
          </div>
        )}
        {chat.error && (
          <div
            className="anim-scale-in p-3 rounded-2xl flex items-center gap-2"
            style={{ background: C.blush, color: C.vermillionDeep }}
            role="alert"
          >
            <span className="ff-body text-[12px] flex-1">{chat.error}</span>
            <button
              type="button"
              onClick={chat.retry}
              className="px-2 py-1 rounded-full ff-body text-[12px] font-semibold"
              style={{ background: 'white' }}
            >
              <RotateCcw size={12} className="inline -mt-0.5 mr-1" aria-hidden /> Retry
            </button>
          </div>
        )}
      </div>

      {chat.suggestions && chat.suggestions.length > 0 && (
        <ReplyOptions
          options={chat.suggestions}
          accent={persona.accent}
          onSelect={(o) => {
            chat.clearSuggestions();
            void chat.send(o.hz);
          }}
        />
      )}

      <div className="flex items-center gap-2 px-4 pb-1">
        {chat.suggestionsError && (
          <span
            className="ff-body text-[11px]"
            style={{ color: C.vermillionDeep }}
            role="alert"
          >
            {chat.suggestionsError}
          </span>
        )}
        <button
          type="button"
          onClick={() => void chat.fetchSuggestions()}
          disabled={chat.suggestionsLoading || chat.loading}
          className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ff-body text-[11px] font-semibold disabled:opacity-60"
          style={{ background: C.paper, color: C.inkLight }}
          aria-label={chat.suggestionsLoading ? 'Thinking of replies' : 'Suggest replies'}
        >
          <Sparkles size={11} aria-hidden />
          {chat.suggestionsLoading ? (
            <span className="inline-flex items-center gap-1" aria-hidden>
              <span className="w-1.5 h-1.5 rounded-full anim-bounce-dot" style={{ background: persona.accent }} />
              <span
                className="w-1.5 h-1.5 rounded-full anim-bounce-dot"
                style={{ background: persona.accent, animationDelay: '0.2s' }}
              />
              <span
                className="w-1.5 h-1.5 rounded-full anim-bounce-dot"
                style={{ background: persona.accent, animationDelay: '0.4s' }}
              />
            </span>
          ) : (
            <>
              Suggest replies
              <span className="ff-zh-serif opacity-80 ml-0.5">· 建议回复</span>
            </>
          )}
        </button>
      </div>

      <TypingReply
        accent={persona.accent}
        disabled={chat.loading}
        onSend={(t) => void chat.send(t)}
      />

      {profileOpen && (
        <PersonaProfileSheet persona={persona} onClose={() => setProfileOpen(false)} />
      )}
    </section>
  );
}
